import React from 'react';
import { Building2, FileText, Grid3X3 } from 'lucide-react';
import { EditableCell } from './EditableCell';
import { getSectionColor, getDataTypeInfo } from '../utils/colors';

interface FormViewProps {
  data: {
    columns: Array<{
      name: string;
      columnId: number;
      type: 'data' | 'número' | 'texto' | 'categoria_mercado' | 'yesno' | 'status_prospeccao' | 'status_industria' | 'percent';
      subsection: string;
      section: string;
      sectionId?: number;
    }>;
    rows: Array<Record<string, { value: string; valueId: number; columnId: number; type: string }>>;
  };
  selectedIndustry: string;
  onCellSave: (valueId: number, columnId: number, newValue: string, industryId?: number) => Promise<boolean>;
  sectionsData?: Record<string, { sectionId: number }>;
  onShowCellHistory?: (columnId: number, industryId: number, columnName: string, industryName: string) => void;
  editEnabled?: boolean;
}

export const FormView: React.FC<FormViewProps> = ({ data, selectedIndustry, onCellSave, sectionsData, onShowCellHistory, editEnabled = true }) => {
  // Group columns by section
  const groupedColumns = data.columns.reduce((acc, column) => {
    if (!acc[column.section]) {
      acc[column.section] = [];
    }
    acc[column.section].push(column);
    return acc;
  }, {} as Record<string, typeof data.columns>);

  if (selectedIndustry) {
    // Single industry form view
    const row = data.rows[0];
    if (!row) return null;

    return (
      <div className="space-y-8">
        {Object.entries(groupedColumns).map(([sectionName, columns]) => {
          const sectionId = sectionsData?.[sectionName]?.sectionId || 0;
          const sectionColors = getSectionColor(sectionId);
          
          return (
          <div key={sectionName} className={`bg-white rounded-xl shadow-sm border-2 ${sectionColors.border}`}>
            <div className={`px-6 py-4 border-b ${sectionColors.border} ${sectionColors.bg}`}>
              <div className="flex items-center space-x-3">
                <FileText className={`h-5 w-5 ${sectionColors.icon}`} />
                <h3 className={`text-lg font-semibold ${sectionColors.text}`}>{sectionName}</h3>
                
              </div>
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {columns.map((column) => {
                  const cellData = row[column.name];
                  return (
                    <div key={column.name} className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        <div className="flex items-center space-x-2">
                          <span>{column.name}</span>
                          <span 
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getDataTypeInfo(column.type).bgColor} ${getDataTypeInfo(column.type).color}`}
                            title={`Tipo: ${getDataTypeInfo(column.type).label}`}
                          >
                            {getDataTypeInfo(column.type).icon}
                          </span>
                        </div>
                        <span className="text-xs text-gray-500 ml-2">
                          ({column.subsection})
                        </span>
                      </label>
                      <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                        <EditableCell
                          value={cellData?.value || ''}
                          type={cellData?.type as 'data' | 'número' | 'texto' | 'categoria_mercado' | 'yesno' | 'status_prospeccao' | 'status_industria' | 'percent'}
                          onSave={(newValue) => 
                            onCellSave(cellData?.valueId || 0, cellData?.columnId || 0, newValue)
                          }
                          disabled={!cellData?.valueId || !editEnabled}
                          onShowHistory={cellData?.valueId && onShowCellHistory ? () => {
                            onShowCellHistory(cellData.columnId, row.id.valueId, column.name, selectedIndustry);
                          } : undefined}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          );
        })}
      </div>
    );
  }

  // Multiple industries card view
  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-3 mb-6">
        <Grid3X3 className="h-6 w-6 text-blue-600" />
        <h2 className="text-xl font-semibold text-gray-900">Visão Geral das Indústrias</h2>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {data.rows.map((row) => (
          <div key={row.id.value} className="bg-white rounded-xl shadow-sm border hover:shadow-md transition-shadow">
            <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0 h-10 w-10 bg-blue-600 rounded-full flex items-center justify-center">
                  <Building2 className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{row.id.value}</h3>
                  <p className="text-sm text-gray-600">Indústria</p>
                </div>
              </div>
            </div>
            
            <div className="p-6">
              <div className="space-y-4">
                {Object.entries(groupedColumns).slice(0, 3).map(([sectionName, columns]) => (
                  <div key={sectionName}>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">{sectionName}</h4>
                    <div className="space-y-2">
                      {columns.slice(0, 2).map((column) => {
                        const cellData = row[column.name];
                        return (
                          <div key={column.name} className="flex justify-between items-center text-sm">
                            <span className="text-gray-600">{column.name}:</span>
                            <span className="font-medium text-gray-900">
                              {cellData?.value || 'N/A'}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
                
                {Object.keys(groupedColumns).length > 3 && (
                  <div className="pt-2 border-t border-gray-100">
                    <p className="text-xs text-gray-500 text-center">
                      +{Object.keys(groupedColumns).length - 3} seções adicionais
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};